import { useMutation, useQueryClient } from '@tanstack/react-query';
import { container } from '@src/container';
import { AuthService } from '@src/application/users/usecases';
import { ToastService } from '@src/shared/services/toast.service';

/**
 * Hook: useSetUniversity
 * Sets user's university during onboarding
 * Only works when profile_state is UNIVERSITY_REQUIRED
 * 
 * @example
 * const { mutate: setUniversity, isPending } = useSetUniversity();
 * setUniversity({ universityId: '123e4567-e89b-12d3-a456-426614174000' });
 */
export function useSetUniversity() {
    const authService = container.resolve<AuthService>('authService');
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ universityId }: { universityId: string }) => {
            return await authService.setUniversity(universityId);
        },
        onSuccess: () => {
            // Invalidate current user to refresh profile state
            queryClient.invalidateQueries({ queryKey: ['auth', 'currentUser'] });
            ToastService.success('University set successfully!');
        },
        onError: (error) => {
            const errorMessage = error instanceof Error ? error.message : 'Failed to set university';
            ToastService.error(errorMessage);
        },
        retry: false,
    });
}
